import { gql } from '@apollo/client';

/** Public SAML config returned by getPublicSamlCfgByAccountId */
export interface PublicSamlCfg {
  enabled: boolean;
  enablePasswordLogin: boolean;
  entryPoint: string | null;
  providerId: string | null;
  signInButtonLabel: string;
}

/** Result type for GET_PUBLIC_SAML_CFG query */
export interface GetPublicSamlCfgResult {
  getPublicSamlCfgByAccountId: PublicSamlCfg | null;
}

/** Variables type for GET_PUBLIC_SAML_CFG query */
export interface GetPublicSamlCfgVariables {
  accountId: string;
}

/**
 * Get public SAML config by accountId (no auth required).
 * Used by LoginPage to show/hide SAML button.
 * Returns { enabled, enablePasswordLogin, entryPoint, providerId, signInButtonLabel }.
 */
export const GET_PUBLIC_SAML_CFG = gql`
  query GetPublicSamlCfgByAccountId($accountId: String!) {
    getPublicSamlCfgByAccountId(accountId: $accountId)
  }
`;

/** Shape returned by findSSOLoginByHost — backend returns untyped JSON, we type the fields we use */
export interface SsoLoginByHostResponse {
  providerId?: string;
  entryPoint?: string;
  samlSSOUrl?: string;
}

/** Result type for FIND_SSO_LOGIN_BY_HOST query */
export interface FindSsoLoginByHostResult {
  findSSOLoginByHost: SsoLoginByHostResponse | null;
}

/** Variables type for FIND_SSO_LOGIN_BY_HOST query */
export interface FindSsoLoginByHostVariables {
  host: string;
}

/**
 * Detect SSO provider by hostname (no auth required).
 * Used by SamlLoginPage no-token fallback (mainly Flutter mobile app).
 */
export const FIND_SSO_LOGIN_BY_HOST = gql`
  query FindSSOLoginByHost($host: String!) {
    findSSOLoginByHost(host: $host)
  }
`;

/** Result type for CREATE_FIREBASE_CUSTOM_TOKEN mutation */
export interface CreateFirebaseCustomTokenResult {
  createFirebaseCustomToken: string | null;
}

/** Variables type for CREATE_FIREBASE_CUSTOM_TOKEN mutation */
export interface CreateFirebaseCustomTokenVariables {
  serviceName: string;
}

/**
 * Create an encrypted Firebase custom token for the authenticated user.
 * Used by SamlAppLoginPage to deliver token to Flutter mobile app.
 * Requires authentication — user must be signed in via Firebase first.
 * Backend encrypts the token with AES-CBC before returning.
 */
export const CREATE_FIREBASE_CUSTOM_TOKEN = gql`
  mutation CreateFirebaseCustomToken($serviceName: String!) {
    createFirebaseCustomToken(serviceName: $serviceName)
  }
`;

// --- SAML Settings (authenticated, admin-only) ---

/** Role mapping entry for SAML group → team/role */
export interface RoleMapping {
  groupName: string;
  roleId: string;
  teamId: string;
}

/** Default mapping for users not in any mapped group */
export interface UnAssignedMapping {
  roleId: string;
  teamId: string;
}

/** Full SAML configuration returned by getSamlCfg */
export interface SamlCfg {
  _id: string;
  enabled: boolean;
  enableSPInitiated: boolean;
  entryPoint: string | null;
  issuer: string | null;
  cert: string | null;
  audienceURI: string | null;
  signInButtonLabel: string | null;
  firstNameAttr: string | null;
  lastNameAttr: string | null;
  groupsAttr: string | null;
  enablePasswordLogin: boolean;
  autoCreateAccount: boolean;
  overwriteUser: boolean;
  providerId: string | null;
  enableProvisioning: boolean;
  overwriteUserMembership: boolean;
  unAssignedMapping: UnAssignedMapping | null;
  roleMappings: RoleMapping[];
}

/** Result type for GET_SAML_CFG query */
export interface GetSamlCfgResult {
  getSamlCfg: SamlCfg | null;
}

/**
 * Get full SAML configuration (admin-only, authenticated).
 * Used by SamlSettingsPage.
 */
export const GET_SAML_CFG = gql`
  query GetSamlCfg {
    getSamlCfg {
      _id
      enabled
      enableSPInitiated
      entryPoint
      issuer
      cert
      audienceURI
      signInButtonLabel
      firstNameAttr
      lastNameAttr
      groupsAttr
      enablePasswordLogin
      autoCreateAccount
      overwriteUser
      providerId
      enableProvisioning
      overwriteUserMembership
      unAssignedMapping {
        roleId
        teamId
      }
      roleMappings {
        groupName
        roleId
        teamId
      }
    }
  }
`;

/** Variables type for SAVE_SAML_CFG mutation */
export interface SaveSamlCfgVariables {
  payload: Partial<Omit<SamlCfg, '_id'>> & { _id?: string | null };
}

/** Result type for SAVE_SAML_CFG mutation */
export interface SaveSamlCfgResult {
  saveSamlCfg: SamlCfg | null;
}

/**
 * Update SAML configuration (admin-only, authenticated).
 * Backend syncs changes to Firebase SAML provider.
 */
export const SAVE_SAML_CFG = gql`
  mutation SaveSamlCfg($payload: SamlCfgsInput!) {
    saveSamlCfg(payload: $payload) {
      _id
      enabled
      enableSPInitiated
      entryPoint
      issuer
      cert
      audienceURI
      signInButtonLabel
      firstNameAttr
      lastNameAttr
      groupsAttr
      enablePasswordLogin
      autoCreateAccount
      overwriteUser
      providerId
      enableProvisioning
      overwriteUserMembership
      unAssignedMapping {
        roleId
        teamId
      }
      roleMappings {
        groupName
        roleId
        teamId
      }
    }
  }
`;

// --- Custom Roles (for group mapping dropdowns) ---

/** Custom role returned by customRoles query */
export interface CustomRole {
  _id: string;
  name: string;
  teamId: string | null;
}

/** Result type for GET_CUSTOM_ROLES query */
export interface GetCustomRolesResult {
  customRoles: {
    page: {
      edges: Array<{ node: CustomRole }>;
    };
  };
}

/**
 * Get custom roles for SAML group mapping.
 * Used alongside system roles (ROLE_TYPES) in mapping dropdowns.
 */
export const GET_CUSTOM_ROLES = gql`
  query GetCustomRoles($first: Int) {
    customRoles(first: $first) {
      page {
        edges {
          node {
            _id
            name
            teamId
          }
        }
      }
    }
  }
`;
